import { useUserSlice } from "@/models/useUserSlice";
import { AuthUser } from "@/models/types";
import { Router } from "@/_router";
import { getBasePath } from "@/models/utils";
import * as serverClient from "@/models/serverClient";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import toast from "react-hot-toast";
import { useUISlice } from "@/models/useUISlice";
import { getPlatform, isNativeApp } from "@/models/platform";
import { i18n } from "@/locales";
import { logFunnelEvent } from "@/fetchData/datadog";
import { datadogLogs } from "@datadog/browser-logs";
import { initPostHog } from "@/fetchData/posthog";

await SocialLogin.initialize({
  google: {
    webClientId: import.meta.env.VITE_PUBLIC_GOOGLE_CLIENT_ID, // Use Web Client ID for all platforms
    iOSClientId: import.meta.env.VITE_PUBLIC_GOOGLE_IOS_CLIENT_ID, // for iOS
  },
  facebook: {
    appId: import.meta.env.VITE_PUBLIC_FACEBOOK_APP_ID,
    clientToken: import.meta.env.VITE_PUBLIC_FACEBOOK_CLIENT_TOKEN,
  },
});

export const useAuth = () => {
  return {
    signIn: useAuth.signIn,
    signInWithGoogle: useAuth.signInWithGoogle,
    signInWithApple: useAuth.signInWithApple,
    currentAuthUser: useAuth.currentAuthUser,
    isAuthenticated: useAuth.isAuthenticated,
    signOut: useAuth.signOut,
  };
};

useAuth.signInWithGoogleWeb = async (): Promise<{ accessToken: string }> => {
  return new Promise((resolve, reject) => {
    const client = (window as any)?.google?.accounts?.oauth2["initTokenClient"](
      {
        client_id: import.meta.env.VITE_PUBLIC_GOOGLE_CLIENT_ID,
        scope: `openid profile email`,
        callback: (response: { access_token: string }) => {
          resolve({ accessToken: response.access_token });
        },
        error_callback: (nonOAuthError: any) => {
          reject(nonOAuthError);
        },
      }
    );
    client.requestAccessToken();
  });
};

useAuth.signInWithGoogle = async ({
  callback,
}: {
  callback?: (authToken: string) => void;
}) => {
  if (useAuth.isAuthenticated()) {
    return callback?.(useUserSlice.getState().userToken);
  }

  try {
    let _accessToken: string;
    if (isNativeApp()) {
      const googleResponse = await SocialLogin.login({
        provider: "google",
        options: {
          scopes: ["email", "profile"],
          forcePrompt: true,
          forceRefreshToken: true,
        },
      });
      _accessToken =
        googleResponse.result.responseType === "online"
          ? googleResponse.result.accessToken?.token
          : "";
    } else {
      const googleResponse = await useAuth.signInWithGoogleWeb();
      _accessToken = googleResponse.accessToken;
    }

    const authenticateResponse = await serverClient.authenticateAsync({
      type: "OnGoogleSignIn",
      payload: {
        googleAccessToken: _accessToken,
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deviceLanguage: navigator.language.toLowerCase(),
        deviceType: getPlatform(),
      },
    });

    if (authenticateResponse.error) {
      return toast.error(i18n.t("useAuth.invalidEmail"));
    }

    const { accessToken } = authenticateResponse.data;

    useAuth.onAuthSuccess(accessToken, callback);
  } catch (e) {
    if (e?.error !== "popup_closed_by_user") {
    }
  }
};

useAuth.signInWithApple = async ({
  callback,
}: {
  callback?: (authToken: string) => void;
}) => {
  if (useAuth.isAuthenticated()) {
    return callback?.(useUserSlice.getState().userToken);
  }

  try {
    const appleUser = await SignInWithApple.authorize({
      clientId: "com.dktlabs.eznoteai",
      redirectURI: "https://eznote-ai.firebaseapp.com/__/auth/handler",
      scopes: "email",
      state: "12345",
    });

    const authenticateResponse = await serverClient.authenticateAsync({
      type: "OnAppleSignIn",
      payload: {
        appleIdentityToken: appleUser?.response?.identityToken,
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deviceLanguage: navigator.language.toLowerCase(),
        deviceType: getPlatform(),
      },
    });

    if (authenticateResponse.error) {
      return toast.error(i18n.t("useAuth.invalidEmail"));
    }

    const { accessToken } = authenticateResponse.data;

    useAuth.onAuthSuccess(accessToken, callback);
  } catch (e) {}
};

useAuth.isAuthenticated = (): boolean => {
  let token = useUserSlice.getState().userToken;

  if (!token) return false;

  if (token) {
    const authUser = serverClient.parseJwt(token);
    const expirationTime = authUser?.exp ? authUser.exp * 1000 : 0; // Convert to milliseconds
    const isExpired = Date.now() >= expirationTime;

    if (isExpired) return false;
  }

  return true;
};

useAuth.isValidEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

useAuth.onAuthSuccess = async (
  authToken: string,
  callback?: (authToken: string) => void
) => {
  try {
    useUISlice.getState().setAlertPromptSignIn(undefined);
    useUserSlice.getState().setUserToken(authToken);
    const user = useAuth.currentAuthUser(authToken);
    useUserSlice.getState().user().profile();

    initPostHog();

    logFunnelEvent("[1] - user authenticated");

    if (isNativeApp()) {
      import("@revenuecat/purchases-capacitor").then(({ Purchases }) => {
        Purchases.logIn({ appUserID: user.userId });
        Purchases.setEmail({ email: user.email });
        Purchases.setAttributes({ ip: user.ip });
      });
    }
  } catch (e) {
    datadogLogs.logger.error("userAuth: User authentication failed", {
      error: e,
    });
  }

  callback && callback(authToken);
};

useAuth.currentAuthUser = (givenToken?: string): AuthUser | undefined => {
  const userToken = givenToken || useUserSlice.getState().userToken;

  if (!userToken) return undefined;

  const authUser = serverClient.parseJwt(userToken);

  if (authUser.exp < Date.now() / 1000) {
    return undefined;
  }

  return authUser;
};

useAuth.signIn = ({
  forceSignIn = false,
  callback,
}: {
  forceSignIn?: boolean;
  callback?: (authToken: string) => void;
}) => {
  if (useAuth.isAuthenticated()) {
    return callback?.(useUserSlice.getState().userToken);
  }

  const enableAppleSignIn = getPlatform() === "ios";

  useUISlice.getState().setAlertPromptSignIn({
    title: i18n.t("useAuth.title"),
    subtitle: i18n.t("useAuth.subtitle"),
    placeholder: i18n.t("useAuth.signInEmailPlaceholder"),
    hideDismissIcon: forceSignIn,
    hideCancel: forceSignIn,
    onGoogleSignIn: () => {
      useAuth.signInWithGoogle({
        callback: (authToken) => {
          useAuth.onAuthSuccess(authToken, callback);
        },
      });
    },
    onAppleSignIn: enableAppleSignIn
      ? () => {
          useAuth.signInWithApple({
            callback: (authToken) => {
              useAuth.onAuthSuccess(authToken, callback);
            },
          });
        }
      : undefined,
    shouldPersistBackdropClick: true,
    button: {
      text: i18n.t("useAuth.signInButton"),
      onPress: async (input) => {
        const email = input.trim().toLowerCase();
        const validEmail = useAuth.isValidEmail(email);

        if (!validEmail) {
          return toast.error(i18n.t("useAuth.invalidEmail"));
        }

        useUISlice.getState().setAlertPromptSignIn({
          ...useUISlice.getState().alertPromptSignIn,
          onGoogleSignIn: undefined,
          defaultValue: email,
          loading: true,
        });

        const otpRequestResponse = await serverClient.authenticateAsync({
          type: "OnOneTimeCodeRequested",
          payload: {
            email,
            deviceType: getPlatform(),
            deviceLanguage: navigator.language,
            deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        });

        useUISlice.getState().setAlertPromptSignIn({
          ...useUISlice.getState().alertPromptSignIn,
          onGoogleSignIn: undefined,
          loading: false,
        });

        if (otpRequestResponse?.error) {
          return toast.error(i18n.t("useAuth.invalidEmail"));
        }

        useUISlice.getState().setAlertPromptSignIn({
          title: i18n.t("useAuth.oneTimeCodeTitle"),
          subtitle: i18n.t("useAuth.oneTimeCodeSubtitle", { email }),
          placeholder: i18n.t("useAuth.oneTimeCodePlaceholder"),
          hideCancel: true,
          hideDismissIcon: forceSignIn,
          shouldPersistBackdropClick: true,
          button: {
            text: i18n.t("useAuth.oneTimeCodeButton"),
            onPress: async (input) => {
              useUISlice.getState().setAlertPromptSignIn({
                ...useUISlice.getState().alertPromptSignIn,
                defaultValue: input,
                loading: true,
              });

              const otpVerifyResponse = await serverClient.authenticateAsync({
                type: "OnOneTimeCodeSubmit",
                payload: { email, oneTimeCode: parseInt(input) },
              });

              useUISlice.getState().setAlertPromptSignIn({
                ...useUISlice.getState().alertPromptSignIn,
                loading: false,
              });

              if (
                otpVerifyResponse &&
                otpVerifyResponse.error === "InvalidOneTimeCode"
              ) {
                return toast.error(i18n.t("useAuth.invalidCode"));
              }

              if (otpVerifyResponse && otpVerifyResponse.data) {
                const { accessToken } = otpVerifyResponse.data;

                await useAuth.onAuthSuccess(accessToken, callback);
              }
            },
          },
        });
      },
    },
  });
};

useAuth.signOut = (callback?: () => void) => {
  useUserSlice.getState().setUserToken(undefined);
  useUserSlice.getState().reset();
  Router.push(`${getBasePath()}`);
  window.location.reload();
  callback && callback();
};
